// src/features/auth/api/authApi.ts
import authService, { LoginCredentials, RegisterData, User } from '../../../services/authService';
import oauthService from '../../../services/oauthService';
import useAuthStore from '../../../store/authStore';
import { GoogleCredentialResponse, OAuthError } from '../../../types/oauth';

// Login handler
export const loginUser = async (credentials: LoginCredentials): Promise<User> => {
  const { setLoading, setUser, setError } = useAuthStore.getState();
  
  try {
    setLoading(true);
    const user = await authService.login(credentials);
    setUser(user);
    return user;
  } catch (error: any) {
    // Make sure we're handling errors as strings
    const errorMessage = typeof error === 'object' ? error.message : String(error);
    setError(errorMessage);
    throw new Error(errorMessage);
  } finally {
    setLoading(false);
  }
};

// Register handler
export const registerUser = async (data: RegisterData): Promise<User> => {
  const { setLoading, setUser, setError } = useAuthStore.getState();
  
  try {
    setLoading(true);
    const user = await authService.register(data);
    setUser(user);
    return user;
  } catch (error: any) {
    // Handle both string errors and object errors {message, code}
    const rawError = error.response?.data?.error;
    const errorMessage = typeof rawError === 'object'
      ? rawError?.message || 'Registration failed. Please try again.'
      : rawError || 'Registration failed. Please try again.';
    setError(errorMessage);
    throw new Error(errorMessage);
  } finally {
    setLoading(false);
  }
};

// Logout handler
export const logoutUser = (): void => {
  const { clearUser } = useAuthStore.getState();
  
  authService.logout();
  clearUser();
};

// Password reset request handler
export const requestPasswordReset = async (email: string): Promise<void> => {
  const { setLoading, setError } = useAuthStore.getState();
  
  try {
    setLoading(true);
    await authService.forgotPassword(email);
  } catch (error: any) {
    const errorMessage = error.response?.data?.error || 'Failed to request password reset. Please try again.';
    setError(errorMessage);
    throw new Error(errorMessage);
  } finally {
    setLoading(false);
  }
};

// Password reset handler
export const resetPassword = async (token: string, newPassword: string): Promise<void> => {
  const { setLoading, setError } = useAuthStore.getState();

  try {
    setLoading(true);
    await authService.resetPassword(token, newPassword);
  } catch (error: any) {
    const errorMessage = error.response?.data?.error || 'Failed to reset password. Please try again.';
    setError(errorMessage);
    throw new Error(errorMessage);
  } finally {
    setLoading(false);
  }
};

// Email-verification handler — redeem the token from the verify-email
// link and land the user signed-in (the backend returns a JWT).
export const verifyEmail = async (token: string): Promise<User> => {
  const { setUser } = useAuthStore.getState();
  const user = await authService.verifyEmail(token);
  if (user) setUser(user);
  return user;
};

// Resend-verification handler — returns { retryAfterSec } when the
// backend's cooldown kicks in; resolves silently otherwise.
export const resendVerification = async (
  email: string,
): Promise<{ retryAfterSec?: number }> => {
  return authService.resendVerification(email);
};

// OAuth Google login handler
export const loginWithGoogle = async (
  credentialResponse: GoogleCredentialResponse
): Promise<{ user: User; isNewUser?: boolean }> => {
  const { setLoading, setUser, setError } = useAuthStore.getState();

  try {
    setLoading(true);
    setError(null);

    const { user, isNewUser } = await oauthService.authenticateWithGoogle(credentialResponse);
    setUser(user);

    return { user, isNewUser };
  } catch (error: unknown) {
    const oauthError = error as OAuthError;
    const errorMessage = oauthError?.message || 'Google sign-in failed. Please try again.';
    setError(errorMessage);
    throw error;
  } finally {
    setLoading(false);
  }
};

// Generic OAuth login handler (for future providers)
export const loginWithOAuth = async (
  provider: string,
  credential: string
): Promise<{ user: User; isNewUser?: boolean }> => {
  const { setLoading, setUser, setError } = useAuthStore.getState();

  try {
    setLoading(true);
    setError(null);

    // This will be expanded as more providers are added
    const { user, isNewUser } = await oauthService.authenticateWithProvider({
      provider: provider as any,
      credential,
    });
    setUser(user);

    return { user, isNewUser };
  } catch (error: unknown) {
    const oauthError = error as OAuthError;
    const errorMessage = oauthError?.message || 'OAuth sign-in failed. Please try again.';
    setError(errorMessage);
    throw error;
  } finally {
    setLoading(false);
  }
};